import { supabase } from "../supabase";
import { mapMembershipRoleToLegacyRole } from "./membershipHelpers";
import {
  getActiveCacheOrganizationId,
  reconcileOfflineCachesForOrganization,
} from "./offlineCacheScope";

export const MEMBERSHIP_CHANGED_EVENT = "cp-membership-changed";

export const INVITATION_STATUS = Object.freeze({
  PENDING: "pending",
  ACCEPTED: "accepted",
  REVOKED: "revoked",
  EXPIRED: "expired",
});

export const INVITABLE_ROLES = Object.freeze([
  { value: "employee", label: "Employee" },
  { value: "manager", label: "Manager" },
  { value: "admin", label: "Organization Admin" },
]);

export const DEFAULT_INVITATION_TTL_DAYS = 7;

const INVITATION_SELECT = `
  id,
  organization_id,
  email,
  role,
  invited_by_member_id,
  token,
  status,
  expires_at,
  accepted_at,
  accepted_by_profile_id,
  created_at,
  updated_at
`;

export function normalizeJoinCode(code) {
  return String(code || "")
    .trim()
    .toUpperCase();
}

export function normalizeInvitationEmail(email) {
  return String(email || "").trim().toLowerCase();
}

export function isInvitationExpired(invitation, asOf = new Date()) {
  if (!invitation?.expires_at) {
    return true;
  }
  return new Date(invitation.expires_at).getTime() < asOf.getTime();
}

export function isInvitationAcceptable(invitation, asOf = new Date()) {
  if (!invitation) {
    return false;
  }
  if (invitation.status !== INVITATION_STATUS.PENDING) {
    return false;
  }
  return !isInvitationExpired(invitation, asOf);
}

export function canInviteRole(actorMembership, targetRole) {
  if (!targetRole || targetRole === "owner") {
    return false;
  }
  if (actorMembership?.role === "owner") {
    return ["admin", "manager", "employee", "guest"].includes(targetRole);
  }
  if (actorMembership?.role === "admin") {
    return ["manager", "employee", "guest"].includes(targetRole);
  }
  return false;
}

export function generateInvitationToken() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID().replace(/-/g, "");
  }
  return `inv-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
}

export function buildInvitationExpiryDate(ttlDays = DEFAULT_INVITATION_TTL_DAYS) {
  const expires = new Date();
  expires.setUTCDate(expires.getUTCDate() + ttlDays);
  return expires.toISOString();
}

/** Merge a created/updated row into a list (newest first, dedupe by id). */
export function mergeInvitationRows(existing = [], created) {
  if (!created?.id) {
    return existing;
  }
  return [created, ...existing.filter((row) => row.id !== created.id)];
}

export function notifyMembershipChanged(detail = {}) {
  if (typeof window === "undefined") {
    return;
  }
  window.dispatchEvent(
    new CustomEvent(MEMBERSHIP_CHANGED_EVENT, { detail }),
  );
}

export async function listOrganizationInvitations(
  organizationId,
  { status = INVITATION_STATUS.PENDING, client = supabase } = {},
) {
  if (!organizationId) {
    return [];
  }

  let query = client
    .from("organization_invitations")
    .select(INVITATION_SELECT)
    .eq("organization_id", organizationId)
    .order("created_at", { ascending: false });

  if (status) {
    query = query.eq("status", status);
  }

  const { data, error } = await query;
  if (error) {
    throw error;
  }
  return data || [];
}

export async function createOrganizationInvitation(
  {
    organizationId,
    email,
    role = "employee",
    invitedByMemberId = null,
    ttlDays = DEFAULT_INVITATION_TTL_DAYS,
  },
  client = supabase,
) {
  const normalizedEmail = normalizeInvitationEmail(email);
  if (!organizationId || !normalizedEmail) {
    throw new Error("Organization and email are required.");
  }
  if (!["admin", "manager", "employee", "guest"].includes(role)) {
    throw new Error("Invalid invitation role.");
  }

  const payload = {
    organization_id: organizationId,
    email: normalizedEmail,
    role,
    invited_by_member_id: invitedByMemberId,
    token: generateInvitationToken(),
    status: INVITATION_STATUS.PENDING,
    expires_at: buildInvitationExpiryDate(ttlDays),
  };

  const { data, error } = await client
    .from("organization_invitations")
    .insert(payload)
    .select(INVITATION_SELECT)
    .single();

  if (error) {
    if (error.code === "23505") {
      throw new Error("A pending invitation already exists for this email.");
    }
    throw error;
  }

  return data;
}

export async function revokeOrganizationInvitation(invitationId, client = supabase) {
  if (!invitationId) {
    throw new Error("Invitation id is required.");
  }

  const { data, error } = await client.rpc("org_v1_revoke_invitation", {
    p_invitation_id: invitationId,
  });

  if (error) {
    throw error;
  }

  return data;
}

export async function acceptOrganizationInvitation(token, client = supabase) {
  const trimmed = String(token || "").trim();
  if (!trimmed) {
    throw new Error("Invitation token is required.");
  }

  const { data, error } = await client.rpc("org_v1_accept_invitation", {
    p_token: trimmed,
  });

  if (error) {
    throw error;
  }

  notifyMembershipChanged({ source: "accept_invitation", result: data });
  return data;
}

export async function resolveJoinCode(code, client = supabase) {
  const { data, error } = await client.rpc("org_v1_resolve_join_code", {
    p_code: normalizeJoinCode(code),
  });

  if (error) {
    throw error;
  }

  return data;
}

export async function joinOrganizationByCode(code, client = supabase) {
  const { data, error } = await client.rpc("org_v1_join_organization_by_code", {
    p_code: normalizeJoinCode(code),
  });

  if (error) {
    throw error;
  }

  notifyMembershipChanged({ source: "join_by_code", result: data });
  return data;
}

/**
 * Switch active organization in local preference + notify App to reload actor.
 */
export function switchActiveOrganization(
  organizationId,
  { setCurrentOrganizationId, memberships = [] } = {},
) {
  if (!organizationId) {
    return null;
  }

  const target = memberships.find((row) => row.organization_id === organizationId);
  if (!target && memberships.length > 0) {
    throw new Error("Organization is not in your membership list.");
  }

  const previousOrganizationId = getActiveCacheOrganizationId();

  setCurrentOrganizationId?.(organizationId);

  reconcileOfflineCachesForOrganization(
    { organization_id: organizationId },
    previousOrganizationId,
  );

  notifyMembershipChanged({
    source: "switch_organization",
    organizationId,
    previousOrganizationId,
    membershipId: target?.id ?? null,
    legacyRole: target ? mapMembershipRoleToLegacyRole(target.role) : null,
  });

  return target;
}

export function parseInviteTokenFromLocation(location = window.location) {
  try {
    const params = new URLSearchParams(location.search);
    return params.get("invite") || params.get("invitation") || null;
  } catch {
    return null;
  }
}

export function clearInviteTokenFromLocation(location = window.location) {
  try {
    const url = new URL(location.href);
    url.searchParams.delete("invite");
    url.searchParams.delete("invitation");
    window.history.replaceState({}, "", url.toString());
  } catch {
    // ignore
  }
}
